import { useTranslation } from "react-i18next";
import { CustomizedBox } from "../../Custom/CustomizedBox";
import { IconButton, Typography } from "@mui/material";
import { useFieldArray, useFormContext } from "react-hook-form";
import { IRole } from "../../../types/Auth/role";
import CheckedUserModal from "../../UI/Modal/CheckedUserModal";
import { useEffect, useRef, useState } from "react";
import { useModal } from "../../../hooks/use-modal";
import { v4 as uuidv4 } from "uuid";
import CustomizedButton from "../../Custom/CustomizedButton";
import AgGrid from "../../UI/AgGrid";
import useUserColumnDefs from "../../Table/User/useUserColumnDefs";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import CloseIcon from "@mui/icons-material/Close";
import { useDisable } from "../../../hooks/use-disable";

const RoleEmployee = () => {
  const { t } = useTranslation();
  const { control } = useFormContext<IRole>();
  const gridRef = useRef();
  const [userIds, setUserIds] = useState<string[]>([]);
  const [userIdsSnapshot, setUserIdsSnapshot] = useState<string[]>([]);

  const [disabled] = useDisable();

  const { modal, openModalHandler, closeModalHandler } = useModal();
  const { fields, replace, remove } = useFieldArray({
    control,
    name: "user_list",
    keyName: "genId",
  });

  const finishUsersSelect = (data: any[]) => {
    // filter out users not selected in current modal session
    let filteredUsers = fields.filter((user: any) =>
      userIds.includes(user.unique_id)
    );

    // get current users id to prevent duplication when appending newly selected users
    const filteredUsersId = filteredUsers.map((user: any) => user.unique_id);

    data.forEach((user) => {
      if (!filteredUsersId.includes(user.unique_id)) {
        filteredUsers.push({ ...user, genId: uuidv4() });
      }
    });

    // sort for proerly render order (and for role assignment when submitting)
    filteredUsers = filteredUsers.sort((a: any, b: any) => {
      return userIds.indexOf(a.unique_id) - userIds.indexOf(b.unique_id);
    });

    const formatUser: any[] = filteredUsers.map((user: any) => ({
      id: user.id,
      unique_id: user.unique_id,
      title_name: user.title_name,
      first_name: user.first_name,
      img_url: user.img_url,
      last_name: user.last_name,
      position: user.position,
      department: user.department,
      email: user.email,
      branch_name: user.branch_name,
      status: user.status,
    }));
    replace(formatUser);
    closeModalHandler();
  };

  useEffect(() => {
    const userUniqueId = fields.map((field) => field.unique_id);
    setUserIds(userUniqueId);
    setUserIdsSnapshot(userUniqueId);
  }, [fields]);

  const columnDefs = useUserColumnDefs(true);

  const formatColumnDefs: ColDef[] = [
    ...columnDefs,
    ...(disabled
      ? []
      : [
          {
            field: "",
            cellRenderer: (params: ICellRendererParams) => (
              <IconButton
                aria-label="delete"
                size="small"
                sx={{ ml: 1, color: "rgba(0, 0, 0, 0.54)" }}
                onClick={() => remove(params.node.rowIndex || 0)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            ),
            width: 50,
            cellStyle: {
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            },
            filter: false,
            sortable: false,
          },
        ]),
  ];

  return (
    <>
      <CustomizedBox margin={"1.5rem 0 0 0"}>
        <Typography fontWeight={600} color={"primary.main"} mb={1}>
          {t("user.role.employee")}
        </Typography>
        <AgGrid
          ref={gridRef}
          columnDefs={formatColumnDefs}
          rowData={fields}
          height={200}
          disabledSidebar
        />
        {!disabled && (
          <CustomizedButton
            title={t("user.role.add_employee")}
            variant="outlined"
            onClick={openModalHandler}
            addIcon
          />
        )}
      </CustomizedBox>
      <CheckedUserModal
        userIds={userIds}
        setUserIds={setUserIds}
        userIdsSnapshot={userIdsSnapshot}
        setUserIdsSnapshot={setUserIdsSnapshot}
        showSelectUser={modal}
        closeUserTable={closeModalHandler}
        finishUsersSelect={finishUsersSelect}
        isRole
        rowSelection="multiple"
      />
    </>
  );
};

export default RoleEmployee;
